import { type Color, Game, type GameStatus, type Variant, getVariant } from '@chessbut/engine';
import {
  type BotOffer,
  type ClientMessage,
  type ClockState,
  type ColorChoice,
  type GameMode,
  type GameSnapshot,
  type LiveStats,
  PROTOCOL_VERSION,
  type PlayerInfo,
  type ServerMessage,
  type TimeControl,
} from '@chessbut/shared';
import { useSyncExternalStore } from 'react';
import { socketUrl } from '../lib/platform.ts';
import { getToken, onTokenChange, refreshSession } from '../lib/session.ts';

export type ConnStatus = 'connecting' | 'online' | 'offline' | 'replaced' | 'outdated' | 'banned';

export interface OnlineGame {
  id: string;
  variant: Variant;
  mode: GameMode;
  rated: boolean;
  white: PlayerInfo;
  black: PlayerInfo;
  /** Null when spectating. */
  you: Color | null;
  timeControl: TimeControl;
  game: Game;
  /** Bumped whenever `game` changes, so React notices the mutation. */
  version: number;
  clock: ClockState | null;
  /** performance.now() when `clock` was received. */
  clockAt: number;
  status: GameStatus;
  drawOffer: Color | null;
  rematchOffer: Color | null;
  connected: [boolean, boolean];
  left: [boolean, boolean];
  /** performance.now() deadline for a disconnected player to return. */
  graceDeadline: number | null;
  ratings: GameSnapshot['ratings'];
  points: GameSnapshot['points'];
}

export interface OnlineState {
  status: ConnStatus;
  playerId: string | null;
  stats: LiveStats | null;
  /** Searching; `bot` is the rated computer offered if nobody else turns up. */
  queue: { variant: string; since: number; bot: BotOffer | null } | null;
  room: { code: string; variant: string; timeControl: TimeControl; color: ColorChoice } | null;
  game: OnlineGame | null;
  error: { code: string; message: string; at: number } | null;
}

/** Messages worth queueing while (re)connecting; game actions resync instead. */
const QUEUEABLE = new Set<ClientMessage['t']>(['queue.join', 'room.create', 'room.join', 'watch']);

export class OnlineClient {
  state: OnlineState = { status: 'connecting', playerId: null, stats: null, queue: null, room: null, game: null, error: null };
  private ws: WebSocket | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly gameStartListeners = new Set<(id: string) => void>();
  private outbox: ClientMessage[] = [];
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;

  constructor(private readonly url: string) {}

  start() {
    if (this.started) return;
    this.started = true;
    this.connect();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.state.status === 'offline') this.reconnectNow();
    });
    window.addEventListener('online', () => this.reconnectNow());
    onTokenChange(() => this.restart());
  }

  /** Reconnects as whoever the session token now says we are. */
  restart() {
    this.outbox = [];
    this.set({ queue: null, room: null, game: null });
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    if (this.state.status === 'replaced' || this.state.status === 'banned') this.set({ status: 'connecting' });
    this.reconnectNow();
  }

  reconnectNow() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retries = 0;
    if (!this.ws) this.connect();
  }

  private set(patch: Partial<OnlineState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private setGame(patch: Partial<OnlineGame>) {
    if (this.state.game) this.set({ game: { ...this.state.game, ...patch } });
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Fires when a new game of ours starts (match found, invite accepted, rematch). */
  onGameStart(listener: (id: string) => void) {
    this.gameStartListeners.add(listener);
    return () => {
      this.gameStartListeners.delete(listener);
    };
  }

  private connect() {
    const token = getToken();
    if (!token) {
      this.set({ status: 'connecting' });
      this.scheduleRetry();
      return;
    }
    this.set({ status: 'connecting' });
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ t: 'hello', token: getToken() ?? '', client: 'web', version: PROTOCOL_VERSION } satisfies ClientMessage));
    };
    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      try {
        this.handle(JSON.parse(event.data as string) as ServerMessage);
      } catch (err) {
        console.error('Bad server message', err);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this.state.status === 'replaced' || this.state.status === 'outdated' || this.state.status === 'banned') return;
      this.set({ status: 'offline', queue: null });
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    if (this.retryTimer) return;
    const delay = Math.min(10_000, 1000 * 2 ** this.retries++);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.ws) this.connect();
    }, delay);
  }

  /** Whether messages go straight to the server right now. */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.state.status === 'online';
  }

  send(msg: ClientMessage) {
    if (this.connected) this.ws!.send(JSON.stringify(msg));
    else if (QUEUEABLE.has(msg.t)) {
      this.outbox = this.outbox.filter((m) => m.t !== msg.t);
      this.outbox.push(msg);
      if (this.state.status === 'offline') this.reconnectNow();
    }
  }

  private fromSnapshot(g: GameSnapshot): OnlineGame | null {
    const variant = getVariant(g.variant);
    if (!variant) return null;
    let game: Game;
    try {
      game = Game.fromMoves(variant, g.moves);
    } catch {
      game = new Game(variant);
    }
    return {
      id: g.id,
      variant,
      mode: g.mode,
      rated: g.rated,
      white: g.white,
      black: g.black,
      you: g.you,
      timeControl: g.timeControl,
      game,
      version: (this.state.game?.version ?? 0) + 1,
      clock: g.clock,
      clockAt: performance.now(),
      status: g.status,
      drawOffer: g.drawOffer,
      rematchOffer: g.rematchOffer,
      connected: g.connected,
      left: g.left,
      graceDeadline: null,
      ratings: g.ratings,
      points: g.points,
    };
  }

  private handle(msg: ServerMessage) {
    switch (msg.t) {
      case 'welcome': {
        this.retries = 0;
        this.set({ status: 'online', playerId: msg.playerId, stats: msg.stats, error: null });
        const queued = this.outbox;
        this.outbox = [];
        for (const m of queued) this.send(m);
        if (this.state.game && this.state.game.you === null) this.send({ t: 'watch', gameId: this.state.game.id });
        this.pingTimer ??= setInterval(() => this.send({ t: 'ping' }), 20_000);
        break;
      }
      case 'stats':
        this.set({ stats: msg.stats });
        break;
      case 'queue':
        this.set({ queue: msg.variant ? { variant: msg.variant, since: msg.since ?? Date.now(), bot: msg.bot ?? null } : null });
        break;
      case 'room':
        this.set({
          room: msg.code && msg.variant && msg.timeControl ? { code: msg.code, variant: msg.variant, timeControl: msg.timeControl, color: msg.color ?? 'random' } : null,
        });
        break;
      case 'game': {
        const next = this.fromSnapshot(msg.game);
        if (!next) break;
        const isNew = next.you !== null && next.id !== this.state.game?.id && !next.status.over;
        this.set({ queue: next.you !== null ? null : this.state.queue, room: next.you !== null ? null : this.state.room, game: next });
        if (isNew) for (const l of this.gameStartListeners) l(next.id);
        break;
      }
      case 'move': {
        const current = this.state.game;
        if (!current || current.id !== msg.gameId) break;
        const { game } = current;
        if (msg.ply === game.ply) {
          if (!game.play(msg.uci)) return this.resync();
        } else if (msg.ply < game.ply) {
          // Our own optimistic move coming back.
          if (game.history[msg.ply]?.uci !== msg.uci) return this.resync();
        } else return this.resync();
        this.setGame({ version: current.version + 1, clock: msg.clock, clockAt: performance.now() });
        break;
      }
      case 'end':
        if (this.state.game?.id === msg.gameId) {
          this.setGame({ status: msg.status, clock: msg.clock, clockAt: performance.now(), drawOffer: null, ratings: msg.ratings, points: msg.points });
        }
        break;
      case 'offers':
        if (this.state.game?.id === msg.gameId) this.setGame({ drawOffer: msg.drawOffer, rematchOffer: msg.rematchOffer });
        break;
      case 'presence':
        if (this.state.game?.id === msg.gameId) {
          this.setGame({
            connected: msg.connected,
            left: msg.left,
            graceDeadline: msg.graceSeconds ? performance.now() + msg.graceSeconds * 1000 : null,
          });
        }
        break;
      case 'error':
        if (msg.code === 'replaced') this.set({ status: 'replaced' });
        else if (msg.code === 'outdated') this.set({ status: 'outdated' });
        else if (msg.code === 'banned') this.set({ status: 'banned', queue: null, room: null });
        else if (msg.code === 'unauthorized') {
          void refreshSession().then(() => this.restart());
          return;
        }
        this.set({ error: { code: msg.code, message: msg.message, at: Date.now() } });
        if (['unknown-variant', 'not-released', 'in-game'].includes(msg.code)) this.set({ queue: null });
        break;
      case 'pong':
        break;
    }
  }

  private resync() {
    if (this.state.game) this.send({ t: 'sync', gameId: this.state.game.id });
  }

  // ---------------------------------------------------------------------------
  // Actions

  quickMatch(variant: string) {
    this.set({ queue: { variant, since: Date.now(), bot: null } });
    this.send({ t: 'queue.join', variant });
  }

  /** Nobody else is searching: take the rated computer game the server offered. */
  playRatedBot() {
    this.send({ t: 'queue.bot' });
  }

  /** Drops queued (not yet sent) messages, e.g. a search cancelled while still connecting. */
  private unqueue(keep: (m: ClientMessage) => boolean) {
    this.outbox = this.outbox.filter(keep);
  }

  cancelQuickMatch() {
    this.set({ queue: null });
    this.unqueue((m) => m.t !== 'queue.join');
    this.send({ t: 'queue.leave' });
  }

  createRoom(variant: string, timeControl: TimeControl, color: ColorChoice) {
    this.send({ t: 'room.create', variant, timeControl, color });
  }

  cancelRoom() {
    this.set({ room: null });
    this.unqueue((m) => m.t !== 'room.create');
    this.send({ t: 'room.cancel' });
  }

  joinRoom(code: string) {
    this.send({ t: 'room.join', code });
  }

  watch(gameId: string) {
    this.send({ t: 'watch', gameId });
  }

  unwatch(gameId: string) {
    if (this.state.game?.id === gameId && this.state.game.you === null) this.set({ game: null });
    this.unqueue((m) => !(m.t === 'watch' && m.gameId === gameId));
    this.send({ t: 'unwatch', gameId });
  }

  /** Plays a move optimistically, then tells the server. Only while connected: moves aren't queued. */
  move(uci: string): boolean {
    const g = this.state.game;
    if (!g || g.you === null || g.status.over || g.game.turn !== g.you || !this.connected) return false;
    const ply = g.game.ply;
    if (!g.game.play(uci)) return false;
    this.setGame({ version: g.version + 1, drawOffer: g.drawOffer === g.you ? g.drawOffer : null });
    this.send({ t: 'move', gameId: g.id, uci, ply });
    return true;
  }

  resign() {
    if (this.state.game) this.send({ t: 'resign', gameId: this.state.game.id });
  }

  abort() {
    if (this.state.game) this.send({ t: 'abort', gameId: this.state.game.id });
  }

  draw(action: 'offer' | 'accept' | 'decline') {
    if (this.state.game) this.send({ t: 'draw', gameId: this.state.game.id, action });
  }

  rematch(action: 'offer' | 'accept' | 'decline') {
    if (this.state.game) this.send({ t: 'rematch', gameId: this.state.game.id, action });
  }

  leaveGame() {
    const g = this.state.game;
    if (g && g.you !== null) this.send({ t: 'leave', gameId: g.id });
    else if (g) this.send({ t: 'unwatch', gameId: g.id });
    this.set({ game: null });
  }

  clearError() {
    this.set({ error: null });
  }
}

export const client = new OnlineClient(socketUrl());

export function useOnline(): OnlineState {
  return useSyncExternalStore(client.subscribe, () => client.state);
}
